import React, { useState, useMemo, useEffect } from 'react'
import { Container, Row, Col, Card, Button, Badge, Alert, Form, Modal } from 'react-bootstrap'
import {
  MapPin,
  Target,
  Anchor,
  Plane,
  Truck,
  Ship,
  Eye,
  RotateCcw,
  CheckCircle,
  ArrowRight,
  Package,
  Trash2,
  AlertTriangle,
} from 'lucide-react'
import styled from 'styled-components'
import { StepComponentProps, SceneFile, SpawnArea, GroupPlacement, LocationData } from '../types'
import MapViewer from '../components/MapViewer'

// Mock scene data - in a real app this would come from an API
const AVAILABLE_SCENES: SceneFile[] = [
  {
    id: 'riviera-florida',
    name: 'Riviera Florida',
    description: 'Coastal Florida scene with marina, waterways, and residential areas',
    filePath: '/usdc/RivieraFlorida_LM_05.usdc',
    previewImage: '/usdc/textures/GOOGLE_SAT_WM_Riviera_clean.png',
    spawnAreas: [
      {
        id: 'marina-water',
        name: 'Marina Waters',
        type: 'water',
        position: { x: -80, y: 0, z: -60 },
        radius: 35,
        description: 'Deep water marina area for large vessels',
      },
      {
        id: 'harbor-water',
        name: 'Harbor Channel',
        type: 'water',
        position: { x: 120, y: 0, z: -20 },
        radius: 25,
        description: 'Main navigation channel',
      },
      {
        id: 'intracoastal-water',
        name: 'Intracoastal Waterway',
        type: 'water',
        position: { x: 20, y: 0, z: -100 },
        radius: 40,
        description: 'Protected inland waterway',
      },
      {
        id: 'parking-area',
        name: 'Marina Parking',
        type: 'land',
        position: { x: -120, y: 0, z: 40 },
        radius: 30,
        description: 'Paved parking area near marina',
      },
      {
        id: 'residential-area',
        name: 'Residential Zone',
        type: 'land',
        position: { x: 80, y: 0, z: 120 },
        radius: 45,
        description: 'Suburban neighborhood streets',
      },
      {
        id: 'commercial-district',
        name: 'Commercial District',
        type: 'land',
        position: { x: -40, y: 0, z: 100 },
        radius: 35,
        description: 'Business and retail area',
      },
      {
        id: 'low-airspace',
        name: 'Low Altitude Zone',
        type: 'air',
        position: { x: 0, y: 150, z: 0 },
        radius: 80,
        description: 'Low altitude flight operations',
      },
      {
        id: 'high-airspace',
        name: 'High Altitude Zone',
        type: 'air',
        position: { x: 0, y: 400, z: 0 },
        radius: 120,
        description: 'High altitude flight corridor',
      },
    ],
  },
]

const StepContainer = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
`

const SceneCard = styled(Card)<{ $selected: boolean }>`
  background: ${(props) => (props.$selected ? 'rgba(128, 0, 133, 0.1)' : '#2a2a2a')};
  border: 1px solid ${(props) => (props.$selected ? '#800085' : '#333')};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${(props) => (props.$selected ? '#800085' : '#555')};
    transform: translateY(-2px);
  }
`

const GroupDragItem = styled.div<{ $isDragging: boolean }>`
  background: #2a2a2a;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  cursor: grab;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.$isDragging ? 0.5 : 1)};

  &:hover {
    border-color: #555;
    transform: translateX(4px);
  }

  &:active {
    cursor: grabbing;
  }
`

const AttachedGroupItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #444;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`

type LocationSelectionStepProps = StepComponentProps

const LocationSelectionStep: React.FC<LocationSelectionStepProps> = ({
  stepData,
  onNext,
  onBack,
}) => {
  const [selectedScene, setSelectedScene] = useState<SceneFile | null>(
    stepData.location?.selectedScene || null
  )
  const [groupPlacements, setGroupPlacements] = useState<GroupPlacement[]>(
    stepData.location?.groupPlacements || []
  )
  const [draggedGroup, setDraggedGroup] = useState<string | null>(null)
  const [actualSpawnAreas, setActualSpawnAreas] = useState<SpawnArea[]>([])
  const [selectedSpawnArea, setSelectedSpawnArea] = useState<SpawnArea | null>(null)
  const [kmlSpawnAreas, setKmlSpawnAreas] = useState<SpawnArea[]>([])
  const [kmlLoaded, setKmlLoaded] = useState(false)

  // Spawn settings for each group placement (groupId-spawnAreaId -> settings)
  const [spawnSettings, setSpawnSettings] = useState<Record<string, { objectCount: number }>>({})

  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false)

  // Compatibility warning modal state
  const [showCompatibilityModal, setShowCompatibilityModal] = useState(false)
  const [pendingDrop, setPendingDrop] = useState<{
    groupId: string
    spawnAreaId: string
    groupName: string
    spawnAreaName: string
    groupBiome: string
    spawnAreaType: string
  } | null>(null)

  // Get all groups from step 1
  const availableGroups = stepData.groups || []

  // Use actual spawn areas if loaded, otherwise use KML spawn areas if loaded, otherwise fallback to scene spawn areas
  const currentSpawnAreas =
    actualSpawnAreas.length > 0
      ? actualSpawnAreas
      : kmlSpawnAreas.length > 0
      ? kmlSpawnAreas
      : selectedScene?.spawnAreas || []

  // Reset selectedSpawnArea if no scene is selected or if selected area no longer exists
  useEffect(() => {
    if (!selectedScene) {
      console.log('🧹 No scene selected - clearing selectedSpawnArea')
      setSelectedSpawnArea(null)
    } else if (
      selectedSpawnArea &&
      !currentSpawnAreas.find((area) => area.id === selectedSpawnArea.id)
    ) {
      console.log(
        '🧹 Selected spawn area no longer exists in currentSpawnAreas - clearing selectedSpawnArea'
      )
      setSelectedSpawnArea(null)
    }
  }, [selectedScene, selectedSpawnArea, currentSpawnAreas])

  // Check if groups are compatible with spawn area types
  const getGroupBiome = (groupId: string) => {
    const group = availableGroups.find((g) => g.id === groupId)
    if (!group) return 'unknown'

    // Simple biome detection based on first object in group
    // In a real app, this would be more sophisticated
    const firstObjectId = group.objects[0]
    if (firstObjectId?.includes('ship') || firstObjectId?.includes('submarine')) return 'water'
    if (firstObjectId?.includes('aircraft') || firstObjectId?.includes('helicopter')) return 'air'
    return 'land'
  }

  const handleSceneSelect = (scene: SceneFile) => {
    setSelectedScene(scene)
    // Reset spawn areas when scene changes - they'll be loaded from GLTF or KML
    setActualSpawnAreas([])
    setKmlSpawnAreas([])
    setSelectedSpawnArea(null)
    setKmlLoaded(false)
  }

  // Load KML spawn areas when scene is selected
  useEffect(() => {
    const loadKMLSpawnAreas = async () => {
      if (!selectedScene) return

      try {
        console.log('🗺️ Loading KML spawn areas for scene:', selectedScene.name)

        const response = await fetch('/KMLs/Riviera Florida.kml')
        if (!response.ok) {
          throw new Error(`Failed to fetch KML: ${response.status}`)
        }

        const kmlText = await response.text()

        // Parse KML
        const parser = new DOMParser()
        const kmlDoc = parser.parseFromString(kmlText, 'text/xml')

        // Check for parsing errors
        const parseError = kmlDoc.getElementsByTagName('parsererror')
        if (parseError.length > 0) {
          throw new Error('Failed to parse KML file')
        }

        const placemarks = kmlDoc.getElementsByTagName('Placemark')
        const newSpawnAreas: SpawnArea[] = []

        console.log(`Found ${placemarks.length} placemarks in KML`)

        for (let i = 0; i < placemarks.length; i++) {
          const placemark = placemarks[i]
          const name =
            placemark.getElementsByTagName('name')[0]?.textContent || `Spawn Area ${i + 1}`
          const description = placemark.getElementsByTagName('description')[0]?.textContent || ''

          // Extract coordinates from KML
          const coordinateElements = placemark.getElementsByTagName('coordinates')
          if (coordinateElements.length > 0) {
            const coordsText = coordinateElements[0].textContent?.trim()
            if (coordsText) {
              // Parse coordinates - KML format is "lng,lat,alt lng,lat,alt ..."
              const coordPairs = coordsText.split(/\s+/).filter((pair) => pair.trim())
              const coordinates: [number, number][] = []

              coordPairs.forEach((pair) => {
                const [lng, lat] = pair.split(',').map(Number)
                if (!isNaN(lng) && !isNaN(lat)) {
                  coordinates.push([lat, lng]) // Leaflet expects [lat, lng]
                }
              })

              if (coordinates.length > 0) {
                // Determine spawn area type from name or description
                const nameDesc = (name + ' ' + description).toLowerCase()
                let type: 'water' | 'land' | 'air' = 'land'

                if (
                  nameDesc.includes('water') ||
                  nameDesc.includes('marina') ||
                  nameDesc.includes('harbor') ||
                  nameDesc.includes('channel') ||
                  nameDesc.includes('ship') ||
                  nameDesc.includes('naval') ||
                  nameDesc.includes('boat') ||
                  nameDesc.includes('dock') ||
                  nameDesc.includes('pier') ||
                  nameDesc.includes('wharf')
                ) {
                  type = 'water'
                } else if (
                  nameDesc.includes('air') ||
                  nameDesc.includes('airport') ||
                  nameDesc.includes('runway') ||
                  nameDesc.includes('aircraft') ||
                  nameDesc.includes('plane')
                ) {
                  type = 'air'
                }

                const spawnArea: SpawnArea = {
                  id: `kml-${i}`,
                  name: name,
                  type: type,
                  position: { x: 0, y: 0, z: 0 }, // These will be set from coordinates
                  radius: 25,
                  description: description,
                  coordinates: coordinates,
                }

                newSpawnAreas.push(spawnArea)
                console.log(
                  `Added spawn area: ${name} (${type}) with ${coordinates.length} coordinates`
                )
              }
            }
          }
        }

        console.log(`Successfully loaded ${newSpawnAreas.length} spawn areas from KML`)
        setKmlSpawnAreas(newSpawnAreas)
        setKmlLoaded(true)
      } catch (error) {
        console.error('Failed to load KML spawn areas:', error)
        setKmlLoaded(false)
      }
    }

    // Load KML when scene is selected (only once per scene)
    if (selectedScene && !kmlLoaded) {
      loadKMLSpawnAreas()
    }
  }, [selectedScene, kmlLoaded])

  const handleSpawnAreaClick = (spawnArea: SpawnArea) => {
    console.log('🖱️ Spawn area clicked:', spawnArea.name, 'ID:', spawnArea.id)
    const newSelection = selectedSpawnArea?.id === spawnArea.id ? null : spawnArea
    console.log('🎯 Setting selectedSpawnArea to:', newSelection?.name || 'null')
    setSelectedSpawnArea(newSelection)
  }

  const handleDragStart = (e: React.DragEvent, groupId: string) => {
    setDraggedGroup(groupId)
    e.dataTransfer.setData('text/plain', groupId)
    console.log('🚀 Drag started for group:', groupId)
  }

  const handleDragEnd = () => {
    setDraggedGroup(null)
  }

  const handleSpawnAreaDrop = (spawnAreaId: string) => {
    console.log('🎯 handleSpawnAreaDrop called:', {
      spawnAreaId,
      draggedGroup,
      selectedScene: selectedScene?.name,
    })

    if (!draggedGroup || !selectedScene) {
      console.log('❌ Missing draggedGroup or selectedScene:', {
        draggedGroup,
        selectedScene: selectedScene?.name,
      })
      return
    }

    // Find spawn area in currentSpawnAreas (from KML), not selectedScene.spawnAreas
    const spawnArea = currentSpawnAreas.find((sa) => sa.id === spawnAreaId)
    const groupBiome = getGroupBiome(draggedGroup)
    const group = availableGroups.find((g) => g.id === draggedGroup)

    console.log('🔍 Spawn area lookup:', {
      spawnAreaId,
      foundSpawnArea: spawnArea?.name,
      spawnAreaType: spawnArea?.type,
      groupBiome,
      totalSpawnAreas: currentSpawnAreas.length,
    })

    if (!spawnArea || !group) {
      console.log('❌ Spawn area or group not found')
      setDraggedGroup(null)
      return
    }

    // Check compatibility
    const isCompatible =
      (spawnArea.type === 'water' && groupBiome === 'water') ||
      (spawnArea.type === 'land' && groupBiome === 'land') ||
      (spawnArea.type === 'air' && groupBiome === 'air')

    if (isCompatible) {
      console.log('✅ Compatible! Creating placement:', { groupId: draggedGroup, spawnAreaId })
      createGroupPlacement(draggedGroup, spawnAreaId)
    } else {
      console.log('⚠️ Incompatible drop - showing warning modal:', {
        spawnArea: spawnArea.name,
        spawnAreaType: spawnArea.type,
        groupBiome,
        groupName: group.name,
      })

      // Store pending drop data and show modal
      setPendingDrop({
        groupId: draggedGroup,
        spawnAreaId: spawnAreaId,
        groupName: group.name,
        spawnAreaName: spawnArea.name,
        groupBiome: groupBiome,
        spawnAreaType: spawnArea.type,
      })
      setShowCompatibilityModal(true)
    }

    setDraggedGroup(null)
  }

  const removeGroupPlacement = (groupId: string, spawnAreaId: string) => {
    setGroupPlacements((prev) =>
      prev.filter((p) => !(p.groupId === groupId && p.spawnAreaId === spawnAreaId))
    )
    // Also remove spawn settings for this specific group-spawnArea combination
    setSpawnSettings((prev) => {
      const newSettings = { ...prev }
      delete newSettings[`${groupId}-${spawnAreaId}`]
      return newSettings
    })
  }

  // Remove ALL placements for a group (from all spawn areas)
  const removeAllGroupPlacements = (groupId: string) => {
    setGroupPlacements((prev) => prev.filter((p) => p.groupId !== groupId))
    // Also remove all spawn settings for this group
    setSpawnSettings((prev) => {
      const newSettings = { ...prev }
      Object.keys(newSettings).forEach((key) => {
        if (key.startsWith(`${groupId}-`)) {
          delete newSettings[key]
        }
      })
      return newSettings
    })
  }

  // Handle updating spawn settings for a group-spawnArea combination
  const updateSpawnSettings = (
    groupId: string,
    spawnAreaId: string,
    settings: { objectCount: number }
  ) => {
    setSpawnSettings((prev) => ({
      ...prev,
      [`${groupId}-${spawnAreaId}`]: settings,
    }))
  }

  // Get spawn settings for a group-spawnArea combination (with defaults)
  const getSpawnSettings = (groupId: string, spawnAreaId: string) => {
    return spawnSettings[`${groupId}-${spawnAreaId}`] || { objectCount: 10 }
  }

  // Create group placement (helper function)
  const createGroupPlacement = (groupId: string, spawnAreaId: string) => {
    const spawnArea = currentSpawnAreas.find((sa) => sa.id === spawnAreaId)
    if (!spawnArea) return

    console.log('✅ Creating placement:', { groupId, spawnAreaId })

    // Check if this exact combination already exists
    const existingPlacement = groupPlacements.find(
      (p) => p.groupId === groupId && p.spawnAreaId === spawnAreaId
    )
    if (existingPlacement) {
      console.log('⚠️ Placement already exists for this group-spawnArea combination')
      return
    }

    // Add new placement (keep existing placements for this group in other spawn areas)
    const newPlacement: GroupPlacement = {
      groupId: groupId,
      spawnAreaId: spawnAreaId,
      position: spawnArea.position,
      rotation: { x: 0, y: 0, z: 0 },
      scale: 1,
    }

    console.log('📦 Adding new placement:', newPlacement)
    setGroupPlacements((prev) => [...prev, newPlacement])
    console.log('📊 Total placements after update:', groupPlacements.length + 1)

    // Set default spawn settings if not exists for this specific combination
    if (!spawnSettings[`${groupId}-${spawnAreaId}`]) {
      updateSpawnSettings(groupId, spawnAreaId, { objectCount: 10 })
    }
  }

  // Handle drop on selected spawn area
  const handleDropOnSelectedArea = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const groupId = e.dataTransfer.getData('text/plain')
    console.log(
      '📦 Drop received - groupId:',
      groupId,
      'selectedSpawnArea:',
      selectedSpawnArea?.name
    )

    if (groupId && selectedSpawnArea) {
      console.log('✅ Processing drop:', groupId, 'onto', selectedSpawnArea.id)
      // Set the draggedGroup state so handleSpawnAreaDrop can use it
      setDraggedGroup(groupId)
      handleSpawnAreaDrop(selectedSpawnArea.id)
      // Set default spawn settings if not exists
      if (!spawnSettings[`${groupId}-${selectedSpawnArea.id}`]) {
        updateSpawnSettings(groupId, selectedSpawnArea.id, { objectCount: 10 })
      }
      // Clear dragged group after drop
      setDraggedGroup(null)
    } else {
      console.log('❌ Drop failed - missing groupId or selectedSpawnArea')
    }
  }

  // Handle modal actions
  const handleProceedAnyway = () => {
    if (pendingDrop) {
      console.log('⚠️ User chose to proceed with incompatible drop')
      createGroupPlacement(pendingDrop.groupId, pendingDrop.spawnAreaId)
    }
    setShowCompatibilityModal(false)
    setPendingDrop(null)
  }

  const handleCancelDrop = () => {
    console.log('❌ User cancelled incompatible drop')
    setShowCompatibilityModal(false)
    setPendingDrop(null)
  }

  const handleContinue = () => {
    if (!selectedScene) return

    const locationData: LocationData = {
      selectedScene,
      groupPlacements,
      environments: [selectedScene.name],
      coordinates: { lat: 26.0, lng: -80.1 }, // Approximate Riviera FL coordinates
    }

    onNext({ location: locationData })
  }

  // Group placements by spawn area for display
  const placementsBySpawnArea = useMemo(() => {
    const result: Record<string, GroupPlacement[]> = {}
    groupPlacements.forEach((placement) => {
      if (!result[placement.spawnAreaId]) {
        result[placement.spawnAreaId] = []
      }
      result[placement.spawnAreaId].push(placement)
    })
    return result
  }, [groupPlacements])

  const getSpawnAreaIcon = (type: 'water' | 'land' | 'air') => {
    switch (type) {
      case 'water':
        return <Anchor size={20} />
      case 'land':
        return <Truck size={20} />
      case 'air':
        return <Plane size={20} />
    }
  }

  return (
    <StepContainer>
      <Container fluid className="h-100 p-4">
        <div className="mb-4">
          <h3 className="text-white mb-2">Location Selection</h3>
          <p className="text-muted">
            Choose a scene and place your object groups in appropriate spawn areas
          </p>
        </div>

        <Row className="h-100">
          {/* Scene Selection */}
          <Col lg={2}>
            <div className="mb-4">
              <h5 className="text-white mb-3">Available Scenes</h5>
              {AVAILABLE_SCENES.map((scene) => (
                <SceneCard
                  key={scene.id}
                  $selected={selectedScene?.id === scene.id}
                  onClick={() => handleSceneSelect(scene)}
                  className="mb-3"
                >
                  <Card.Body>
                    <div className="d-flex align-items-center mb-2">
                      <MapPin size={16} className="me-2" style={{ color: '#666' }} />
                      <Card.Title className="text-white mb-0 h6">{scene.name}</Card.Title>
                      {selectedScene?.id === scene.id && (
                        <CheckCircle size={16} className="ms-auto" style={{ color: '#800085' }} />
                      )}
                    </div>
                    <Card.Text className="text-muted small">{scene.description}</Card.Text>
                    <div className="d-flex gap-1 mt-2">
                      {scene.spawnAreas
                        .reduce((types, area) => {
                          if (!types.includes(area.type)) types.push(area.type)
                          return types
                        }, [] as string[])
                        .map((type) => (
                          <Badge
                            key={type}
                            style={{
                              background:
                                type === 'water'
                                  ? '#1e40af'
                                  : type === 'land'
                                  ? '#15803d'
                                  : '#d97706',
                              fontSize: '0.6rem',
                            }}
                          >
                            {type}
                          </Badge>
                        ))}
                    </div>
                  </Card.Body>
                </SceneCard>
              ))}
            </div>
          </Col>

          {/* Scene Preview & Group Placement */}
          <Col lg={8}>
            {selectedScene ? (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="text-white mb-0">Scene Preview - {selectedScene.name}</h5>
                  <Badge bg="primary">{groupPlacements.length} groups placed</Badge>
                </div>

                <MapViewer
                  spawnAreas={currentSpawnAreas}
                  groupPlacements={groupPlacements}
                  onSpawnAreaDrop={handleSpawnAreaDrop}
                  draggedGroup={draggedGroup}
                  onSpawnAreaClick={handleSpawnAreaClick}
                  selectedSpawnArea={selectedSpawnArea}
                  isDragOver={isDragOver}
                  onDragOver={() => setIsDragOver(true)}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleDropOnSelectedArea}
                  availableGroups={availableGroups}
                  getSpawnSettings={getSpawnSettings}
                  updateSpawnSettings={updateSpawnSettings}
                  removeGroupPlacement={removeGroupPlacement}
                />

                {/* Scene Overview Panel */}
                <Card className="mt-3" style={{ background: '#2a2a2a', border: '1px solid #333' }}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="text-white mb-1 d-flex align-items-center">
                          <Eye size={16} className="me-2" style={{ color: '#60a5fa' }} />
                          Scene Overview - {selectedScene.name}
                        </h6>
                        <p className="text-muted small mb-0">
                          {currentSpawnAreas.length} spawn areas • {groupPlacements.length} groups
                          placed
                        </p>
                      </div>
                      <Badge bg={groupPlacements.length > 0 ? 'success' : 'secondary'}>
                        {groupPlacements.length} placed
                      </Badge>
                    </div>

                    {(() => {
                      // Calculate scene-wide statistics
                      const totalObjects = groupPlacements.reduce((sum, placement) => {
                        const settings = getSpawnSettings(placement.groupId, placement.spawnAreaId)
                        return sum + (settings.objectCount || 1)
                      }, 0)

                      // Get unique spawn areas that have groups attached
                      const usedSpawnAreaIds = [
                        ...new Set(groupPlacements.map((p) => p.spawnAreaId)),
                      ]
                      const usedSpawnAreas = currentSpawnAreas.filter((area) =>
                        usedSpawnAreaIds.includes(area.id)
                      )

                      const waterAreasUsed = usedSpawnAreas.filter(
                        (area) => area.type === 'water'
                      ).length
                      const landAreasUsed = usedSpawnAreas.filter(
                        (area) => area.type === 'land'
                      ).length
                      const airAreasUsed = usedSpawnAreas.filter(
                        (area) => area.type === 'air'
                      ).length

                      const waterPlacements = groupPlacements.filter((p) => {
                        const area = currentSpawnAreas.find((a) => a.id === p.spawnAreaId)
                        return area?.type === 'water'
                      }).length

                      const landPlacements = groupPlacements.filter((p) => {
                        const area = currentSpawnAreas.find((a) => a.id === p.spawnAreaId)
                        return area?.type === 'land'
                      }).length

                      const airPlacements = groupPlacements.filter((p) => {
                        const area = currentSpawnAreas.find((a) => a.id === p.spawnAreaId)
                        return area?.type === 'air'
                      }).length

                      return (
                        <>
                          {/* Scene Statistics */}
                          <Row className="mb-3">
                            <Col xs={4}>
                              <div
                                className="text-center p-2"
                                style={{ background: '#1a1a1a', borderRadius: '6px' }}
                              >
                                <div className="text-white fw-bold" style={{ fontSize: '1.5rem' }}>
                                  {totalObjects}
                                </div>
                                <div className="text-muted small">Total Objects</div>
                              </div>
                            </Col>
                            <Col xs={4}>
                              <div
                                className="text-center p-2"
                                style={{ background: '#1a1a1a', borderRadius: '6px' }}
                              >
                                <div className="text-white fw-bold" style={{ fontSize: '1.5rem' }}>
                                  {groupPlacements.length}
                                </div>
                                <div className="text-muted small">Groups Placed</div>
                              </div>
                            </Col>
                            <Col xs={4}>
                              <div
                                className="text-center p-2"
                                style={{ background: '#1a1a1a', borderRadius: '6px' }}
                              >
                                <div className="text-white fw-bold" style={{ fontSize: '1.5rem' }}>
                                  {usedSpawnAreas.length}
                                </div>
                                <div className="text-muted small">Areas Used</div>
                              </div>
                            </Col>
                          </Row>

                          {/* Biome Breakdown */}
                          <div className="mb-3">
                            <h6 className="text-white mb-2 d-flex align-items-center">
                              <Target size={14} className="me-2" />
                              Biome Distribution
                            </h6>
                            <Row>
                              <Col xs={4}>
                                <div
                                  className="d-flex align-items-center justify-content-between p-2"
                                  style={{
                                    background: 'rgba(96,165,250,0.1)',
                                    borderRadius: '4px',
                                    border: '1px solid #60a5fa',
                                  }}
                                >
                                  <div>
                                    <div className="text-white small fw-bold">Water</div>
                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                      {waterAreasUsed} areas
                                    </div>
                                  </div>
                                  <Badge style={{ background: '#1e40af' }}>{waterPlacements}</Badge>
                                </div>
                              </Col>
                              <Col xs={4}>
                                <div
                                  className="d-flex align-items-center justify-content-between p-2"
                                  style={{
                                    background: 'rgba(74,222,128,0.1)',
                                    borderRadius: '4px',
                                    border: '1px solid #4ade80',
                                  }}
                                >
                                  <div>
                                    <div className="text-white small fw-bold">Land</div>
                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                      {landAreasUsed} areas
                                    </div>
                                  </div>
                                  <Badge style={{ background: '#15803d' }}>{landPlacements}</Badge>
                                </div>
                              </Col>
                              <Col xs={4}>
                                <div
                                  className="d-flex align-items-center justify-content-between p-2"
                                  style={{
                                    background: 'rgba(251,191,36,0.1)',
                                    borderRadius: '4px',
                                    border: '1px solid #fbbf24',
                                  }}
                                >
                                  <div>
                                    <div className="text-white small fw-bold">Air</div>
                                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                      {airAreasUsed} areas
                                    </div>
                                  </div>
                                  <Badge style={{ background: '#d97706' }}>{airPlacements}</Badge>
                                </div>
                              </Col>
                            </Row>
                          </div>

                          {/* Instructions */}
                          {groupPlacements.length === 0 && (
                            <div
                              className="text-center py-3"
                              style={{
                                background: 'rgba(0,123,255,0.1)',
                                borderRadius: '6px',
                                border: '1px dashed #007bff',
                              }}
                            >
                              <Package size={24} style={{ color: '#007bff' }} className="mb-2" />
                              <div className="text-white small fw-bold mb-1">
                                Ready for Group Placement
                              </div>
                              <div className="text-muted small">
                                1. Select a spawn area from the sidebar
                                <br />
                                2. Drag groups from the right panel onto the map
                              </div>
                            </div>
                          )}
                        </>
                      )
                    })()}
                  </Card.Body>
                </Card>
              </div>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100">
                <div className="text-center">
                  <Eye size={48} style={{ color: '#666', marginBottom: '1rem' }} />
                  <h5 className="text-muted">Select a scene to preview</h5>
                  <p className="text-muted">
                    Choose a scene from the left panel to begin placing your groups
                  </p>
                </div>
              </div>
            )}
          </Col>

          {/* Group Management */}
          <Col lg={2}>
            <h5 className="text-white mb-3">Object Groups</h5>

            {availableGroups.length > 0 ? (
              <>
                <div className="mb-3">
                  <div className="small text-muted mb-2">
                    Available Groups ({availableGroups.length})
                  </div>
                  {availableGroups.map((group) => {
                    const isPlaced = groupPlacements.some((p) => p.groupId === group.id)
                    const groupBiome = getGroupBiome(group.id)

                    return (
                      <GroupDragItem
                        key={group.id}
                        $isDragging={draggedGroup === group.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, group.id)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <div className="text-white fw-bold" style={{ fontSize: '0.85rem' }}>
                              {group.name}
                            </div>
                            <div className="small text-muted">
                              {group.objects.length} objects • {groupBiome} biome
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Badge
                              style={{
                                background:
                                  groupBiome === 'water'
                                    ? '#1e40af'
                                    : groupBiome === 'land'
                                    ? '#15803d'
                                    : '#d97706',
                                fontSize: '0.6rem',
                              }}
                            >
                              {groupBiome}
                            </Badge>
                            {isPlaced && (
                              <div className="d-flex align-items-center">
                                <CheckCircle size={14} style={{ color: '#10b981' }} />
                                <Button
                                  variant="link"
                                  size="sm"
                                  onClick={() => removeAllGroupPlacements(group.id)}
                                  style={{
                                    padding: '0.125rem',
                                    color: '#dc3545',
                                    marginLeft: '0.25rem',
                                  }}
                                  title="Remove placement"
                                >
                                  <RotateCcw size={12} />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </GroupDragItem>
                    )
                  })}
                </div>

                {groupPlacements.length > 0 && (
                  <div className="mt-4 pt-3" style={{ borderTop: '1px solid #333' }}>
                    <Button
                      variant="primary"
                      onClick={handleContinue}
                      disabled={!selectedScene}
                      className="w-100"
                      style={{
                        background: 'linear-gradient(135deg, #800085 0%, #b347b8 100%)',
                        border: 'none',
                      }}
                    >
                      Continue to Challenges
                      <ArrowRight size={16} className="ms-2" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Alert variant="warning">
                <div className="small">
                  No groups available. Please complete Step 1 first to create object groups.
                </div>
              </Alert>
            )}
          </Col>
        </Row>
      </Container>

      {/* Compatibility Warning Modal */}
      <Modal show={showCompatibilityModal} onHide={handleCancelDrop} centered backdrop="static">
        <Modal.Header style={{ background: '#1a1a1a', border: '1px solid #333' }}>
          <Modal.Title className="text-white d-flex align-items-center">
            <AlertTriangle size={20} className="me-2" style={{ color: '#fbbf24' }} />
            Biome Compatibility Warning
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#2a2a2a', color: 'white' }}>
          {pendingDrop && (
            <div>
              <Alert
                variant="warning"
                style={{
                  background: 'rgba(251, 191, 36, 0.1)',
                  border: '1px solid #fbbf24',
                  color: '#fbbf24',
                }}
              >
                <div className="d-flex align-items-center mb-2">
                  <AlertTriangle size={16} className="me-2" />
                  <strong>Biome Mismatch Detected</strong>
                </div>
                <div className="small">
                  You're trying to place a <strong>{pendingDrop.groupBiome}</strong> group in a{' '}
                  <strong>{pendingDrop.spawnAreaType}</strong> spawn area.
                </div>
              </Alert>

              <div className="mb-3">
                <h6 className="text-white mb-2">Drop Details:</h6>
                <div className="ps-3">
                  <div className="mb-1">
                    <strong>Group:</strong> {pendingDrop.groupName}
                    <Badge
                      className="ms-2"
                      style={{
                        background:
                          pendingDrop.groupBiome === 'water'
                            ? '#1e40af'
                            : pendingDrop.groupBiome === 'land'
                            ? '#15803d'
                            : '#d97706',
                      }}
                    >
                      {pendingDrop.groupBiome}
                    </Badge>
                  </div>
                  <div className="mb-3">
                    <strong>Spawn Area:</strong> {pendingDrop.spawnAreaName}
                    <Badge
                      className="ms-2"
                      style={{
                        background:
                          pendingDrop.spawnAreaType === 'water'
                            ? '#1e40af'
                            : pendingDrop.spawnAreaType === 'land'
                            ? '#15803d'
                            : '#d97706',
                      }}
                    >
                      {pendingDrop.spawnAreaType}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <h6 className="text-white mb-2">Recommended Compatibility:</h6>
                <div className="small text-muted ps-3">
                  <div>
                    🟦 <strong>Water groups</strong> → Water spawn areas (ships, submarines, boats)
                  </div>
                  <div>
                    🟩 <strong>Land groups</strong> → Land spawn areas (vehicles, infantry,
                    buildings)
                  </div>
                  <div>
                    🟨 <strong>Air groups</strong> → Air spawn areas (aircraft, helicopters, drones)
                  </div>
                </div>
              </div>

              <div className="text-muted small">
                <strong>Note:</strong> You can still proceed, but objects may not behave as expected
                in incompatible environments.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#1a1a1a', border: '1px solid #333' }}>
          <Button variant="secondary" onClick={handleCancelDrop}>
            Cancel Drop
          </Button>
          <Button
            variant="warning"
            onClick={handleProceedAnyway}
            className="d-flex align-items-center"
          >
            <AlertTriangle size={16} className="me-2" />
            Proceed Anyway
          </Button>
        </Modal.Footer>
      </Modal>
    </StepContainer>
  )
}

export default LocationSelectionStep
